/*
* +------------------------------------------------------------------------+
* | Licensed Materials - Property of IBM
* | IBM Cognos Products: Dashboard
* | (C) Copyright IBM Corp. 2018, 2020
* |
* | US Government Users Restricted Rights - Use, duplication or disclosure
* | restricted by GSA ADP Schedule Contract with IBM Corp.
* +------------------------------------------------------------------------+
*/
define([
	'underscore',
	'bi/moser/moser.min',
	'../../apiHelpers/SlotAPIHelper'
], function(
	_,
	MoserJS,
	SlotAPIHelper
) {

	const SOURCE_TYPES_TO_VERIFY_JOIN = [
		MoserJS.default.UseSpecType.FILE.value(),
		MoserJS.default.UseSpecType.DATASET.value()
	];


	class DatasourceUtil {

		/**
		 * We agreed only do client level join for uploaded files and data sets (dataset2),
		 * or a module created from uploaded files or data sets (dataset2).
		 * @returns true if items are from source types in SOURCE_TYPES_TO_VERIFY_JOIN
		 */
		static _shouldVerifyJoin(module, items = [], moserDataSources){
			const newAPI = !!(module.getInterfaceType && module.getImplType);
			if (newAPI) {
				const dataSourceId = module.getId();
				let verifyJoin = true;
				for (let i = 0; i < items.length && verifyJoin; i++) {
					const columnId = items[i];
					let metadataColumn = module.getMetadataColumn(columnId);
					let table = metadataColumn ? metadataColumn.getTableName() : null;
					if (!metadataColumn || !table) {
						// Skip if not a metadata column or metadata column is not from a table, e.g. stand alone model calculations.
						verifyJoin = false;
						break;
					}

					const originalType = moserDataSources.getOriginalSourceType(dataSourceId, columnId);
					if (originalType && SOURCE_TYPES_TO_VERIFY_JOIN.indexOf(originalType) === -1) {
						verifyJoin = false;
						break;
					}
				}
				return verifyJoin;
			} else {
				const theMoserJS = DatasourceUtil.MoserJS_Inst || MoserJS;
				let tables = [];
				return _.find(items, columnId => {
					if (!DatasourceUtil.isMultiMeasuresSeriesOrValue(columnId)){
						let metadataColumn = module.getMetadataColumn(columnId);
						let table = metadataColumn ? metadataColumn.getTable() : null;
						if (!metadataColumn || !table) {
							return true;
						}

						// Do not check again if already checked
						if (tables.indexOf(table.idForExpression) === -1) {
							tables.push(table.idForExpression);
							const useSpec = theMoserJS.default.ModuleUtils.getObjectUseSpec(table);
							if (useSpec) {
								// Return if there is a source type not belongs to the local join
								return SOURCE_TYPES_TO_VERIFY_JOIN.indexOf(useSpec.getType().value()) === -1;
							}
						}
					}

				}) === undefined;
			}
		}

		static isMultiMeasuresSeriesOrValue(itemId) {
			return itemId.indexOf(SlotAPIHelper.MULTI_MEASURES_SERIES) === 0 || itemId.indexOf(SlotAPIHelper.MULTI_MEASURES_VALUE) === 0;
		}


		static getTableRef (dataSource, itemIds) {
			const names = [];
			if (dataSource){
				itemIds.forEach(item => {
					const metadataColumn = dataSource.getMetadataColumn(item);
					const tableName = metadataColumn && metadataColumn.getTableName();
					if (tableName && names.indexOf(tableName) === -1) {
						names.push(tableName);
					}
				});
			}
			return names;
		}

		/**
		 *
		 * @param {object} module - the data source module, either an uploaded file, a dataset, a package, a module, or a model built from an uploaded file, a dataset, a package, or a module
		 * @param {object[]} projectedItems - An array of projected items ids
		 * @param return true if metadata columns referenced by pageContextItemSpec and dataSlots are from:
		 * 1) uploaded files
		 * 2) data sets
		 * 3) the combination of 1) and 2).
		 */
		static isSupportedNonJoinTableDatascourceType(module, itemIds = []) {
			if (!module || module.getSourceType() === 'package' || itemIds.length === 0) {
				return false;
			}
			return DatasourceUtil._shouldVerifyJoin(module, itemIds);
		}

		/**
		 * A dashboard works against one MUI module created on the fly each time a dashboard is created.
		 * A package is a metadata model that have not the necessary table joins applied at the database level.
		 * Therefore dashboards need not worry about these joins.
		 * A dashboard only handle join relationships at the data module level.
		 * MUI creates a module which can be one of:
		 * 1) contain one single package, this is the simplest case
		 * 2) uploaded file containing more than one table where there are no joins between tables. In Endor, all tables within an uploaded xlsx are not joined
		 * 3) a data module containing any combination of packgages, other modules, uploaded files and data sets. This is the complex case.
		 *
		 * For Endor release, decision is made to only verify table joins for uploaded files and data sets since currently there is no API to get accurate
		 * join relationships for case3).
		 *
		  * @param return true if metadata columns referenced by pageContextItemSpec and dataSlots are from:
		 * 1) uploaded files
		 * 2) data sets
		 * 3) the combination of 1) and 2).
		 */
		static mustVerifyJoinTablesInSameDataSource(dataSource, pageContextItemSpec, itemIds = [], moserDataSources) {
			//A module can be a package in which case return false (this is the simples case) OR when pageContextItem has no hierarchy context to work on
			const dataSourceType = dataSource.getType && dataSource.getType() || dataSource.getSourceType && dataSource.getSourceType();
			if (dataSourceType === 'package') {
				return false;
			}

			const pageContextColumnIds = pageContextItemSpec && pageContextItemSpec.hierarchies && pageContextItemSpec.hierarchies.map(h => h.hierarchyUniqueName) ||
						pageContextItemSpec && pageContextItemSpec.getItemIds && pageContextItemSpec.getItemIds();
			if (!_.isArray(pageContextColumnIds) || !pageContextColumnIds.length) {
				return false;
			}

			itemIds.push(...pageContextColumnIds);

			return DatasourceUtil._shouldVerifyJoin(dataSource, itemIds, moserDataSources);
		}

		static haveTableJoinsInSameDataSource (module, tableNames, otherTableNames) {
			// TODO Clean internal function names in other classes.
			const tablesHaveJoinRelationship = module.hasJoinedTables ? module.hasJoinedTables.bind(module) : module.tablesHaveJointRelationship.bind(module);
			let result = _.find(otherTableNames, otherTableName => {
				return _.find(tableNames, tableName => {
					return tablesHaveJoinRelationship(tableName, otherTableName);
				});
			});

			return result ? true : false;
		}

		/**
		* Returns a boolean indicating whether there is a least the same table referenced in both tables
		*
		* @param {array} tableRef1 - table 1 to check
		* @param {array} tableRef2 - the table to check against tableRef1
		*
		* @return {boolean} return true if there is at least one table name matched in both tables, else return false
		*/
		static haveATableReference (tableRef1 = [], tableRef2 = [], exactMatch = false) {
			if ((exactMatch && (tableRef1.length !== tableRef2.length)) || (tableRef1.length === 0 && tableRef2.length === 0)) {
				return false;
			}

			return _.find(tableRef1, ref1 => {
				return tableRef2.indexOf(ref1) !== -1;
			}) ? true : false;
		}

		/**
		* Dynamically calculates and returns the list of table references for the specified page context entry
		*
		* @param {object} module - the module to retrieve the table names
		* @param {object} pageContextEntry - the page context entry
		*
		* @return {array} List of retrieved table names
		*/
		static getPageContextItemTableRef = (dataSource, pageContextEntry) => {
			let tableRef = [];
			let itemIds = pageContextEntry.getItemIds ? pageContextEntry.getItemIds() : _.pluck(pageContextEntry.hierarchies, 'hierarchyUniqueName');

			itemIds.forEach(itemId => {
				let column = dataSource.getMetadataColumn(itemId);
				if (column) {
					let name = column.getTableName();
					if (tableRef.indexOf(name) === -1) {
						tableRef.push(name);
					}
				}
			});
			return tableRef;
		}

		/**
		* Determine whether the slot and dataItem are ordinal and attribute
		*
		* @param {object} slot - The slot item that the function will compare
		* @param {object} dataItem - The dataItem that the function will compare to the slot
		*
		* @return {boolean} A boolean to represent whether or not it is Ordinal vs. Attribute
 		**/
		static isOrdinalAttribute = (slot, dataItem) => {
			let slotType = slot.getDefinition().getType();
			let dataType = dataItem.getMetadataColumn().getType();
			return (slotType === 'ordinal') && (dataType === 'attribute');
		}

		/**
		* Process and return a merged filter page context
		*
		* @param {array} netFilterPageContext - array filter net page context
		* @param {array} synchronizedFilters - array of synchronized filters
		*
		* @return {array} merged synchronized and filter net page context array
		*/
		static mergeSynchronizePageContextFilter (netFilterPageContext, synchronizedFilters) {
			if (!synchronizedFilters || synchronizedFilters.length === 0) {
				return netFilterPageContext;
			}

			let hasFilterByName = (filtersToCheck, filterName) => {
				let exist = _.find(filtersToCheck, filter => {
					return filter.getItemName().toLowerCase() === filterName;
				});
				return exist ? true : false;
			};

				//Populate this vis Filters
			let thisVisFilters = {};

			let getFiltersByScope = (scope) => {
				let array = thisVisFilters[scope] = thisVisFilters[scope] || [];
				return array;
			};

			netFilterPageContext.forEach(entry => {
				let filters = getFiltersByScope(entry.getScope());
				filters.push(entry);
			});

			//Process synch local filters first because they have precedence over global filters
			let localSynchFilters = synchronizedFilters.filter(entry => {
				return entry.scope !== 'global';
			});

			localSynchFilters.forEach(entry => {
				let nameToCheck = entry.filterName.toLowerCase();
				let thisTableFilters = getFiltersByScope(entry.scope);
				if (!hasFilterByName(thisTableFilters, nameToCheck)) {
					thisTableFilters.push(entry.getAPI());
				}
				//Remove the same one in the global if it exists since the thisTab one takes precedence since these ones may have been already defined in this vis so remove them
				thisVisFilters.global = _.filter(thisVisFilters.global, (filter) => {
					return filter.getItemName().toLowerCase() !== nameToCheck;
				});
			});


			//Now process synch global filters
			let globalSynchFilters = synchronizedFilters.filter(entry => {
				return entry.scope === 'global';
			});

			globalSynchFilters.forEach(entry => {
				let nameToCheck = entry.filterName.toLowerCase();
				let toAdd = true;
				//Loop through set of thisTab filters and only add the global filter if it is not defined in the thisTab scope
				for(let property in thisVisFilters) {
					if (property !== 'global' && hasFilterByName(thisVisFilters[property], nameToCheck)) {
						toAdd = false;
						break;
					}
				}

				if(toAdd) {
					let globalFilters = getFiltersByScope('global');
					globalFilters.push(entry.getAPI());
				}
			});

			//Now concat all results and return as a single array
			let result;
			for (let prop in thisVisFilters) {
				if (thisVisFilters[prop] && thisVisFilters[prop].length > 0) {
					if (!result) {
						result = thisVisFilters[prop];
					} else {
						result = result.concat(thisVisFilters[prop]);
					}
				}
			}
			return result || [];
		}

		static metadataColumnSupportsFiltering (column) {
			if (!column) {
				return false;
			}
			// getAggregateMode is from the old object MetadaColumn
			// getAggregationMode is the new API function
			// the column passed in can be either the old MetadaColumn or the new API Column
			const getAggregationMode = (column.getAggregateMode || column.getAggregationMode).bind(column);
			return !(column.getType() === 'fact' && (getAggregationMode() === 'static' || column.getDefaultAggregation() === 'calculated'));
		}
	}

	return DatasourceUtil;
});
